import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';

import { Map as LeafletMap } from 'leaflet';
import Esri from 'esri-leaflet';

import {
  activateMapLoadingStatus,
  deactivateMapLoadingStatus,
} from '../../../store/actions';
import { getArcGisToken } from '../../../ajax/endpoints';
import { esriWorkforceServer } from '../../../config';
import { humanizeNumber } from '../../../utils/format';

import {
  OCCUPATION_DICT,
  INDUSTRY_DICT,
  MIN_WORKFORCE_ZOOM_LEVEL,
  WORKFORCE_LEGEND_BOUNDS,
  WORKFORCE_COLOR_SCALES,
} from '../../../constants';
import LegendBar from '../LegendBar';

// Fetch OCC/IND related records for a "chunk" of objectIds
async function fetchChunk(objectIds, token) {
  const paramStr = objectIds.join(',');
  const q = `outFields=*&returnGeometry=false&f=json&token=${encodeURIComponent(
    token,
  )}`;
  const occUrl = `${esriWorkforceServer}/queryRelatedRecords?objectIds=${paramStr}&relationshipId=0&${q}`;
  const indUrl = `${esriWorkforceServer}/queryRelatedRecords?objectIds=${paramStr}&relationshipId=1&${q}`;

  const [occRes, indRes] = await Promise.all([
    fetch(occUrl).then(r => r.json()),
    fetch(indUrl).then(r => r.json()),
  ]);

  const dataById = {};
  _.get(occRes, 'relatedRecordGroups', []).forEach(g => {
    if (!dataById[g.objectId]) dataById[g.objectId] = { occ: [], ind: [] };
    dataById[g.objectId].occ = g.relatedRecords || [];
  });

  _.get(indRes, 'relatedRecordGroups', []).forEach(g => {
    if (!dataById[g.objectId]) dataById[g.objectId] = { occ: [], ind: [] };
    dataById[g.objectId].ind = g.relatedRecords || [];
  });

  return dataById;
}

// Fetch OCC/IND for a collection of object IDs
// Uses chunking of IDs for large requests
export async function fetchOccAndInd(objectIds, token, chunkSize = 100) {
  const chunks = _.chunk(objectIds, chunkSize);
  const chunkDataArray = await Promise.all(chunks.map(ids => fetchChunk(ids, token)));

  return chunkDataArray.reduce((acc, chunkData) => ({ ...acc, ...chunkData }), {});
}

// Finds the sum of a given occupation/industry on cached objects
// This data has no inherent scale, so this is used to create a relative one.
function getMeasureValue(oid, measure, cache) {
  if (!cache[oid]) return null;

  const rows =
    measure && measure.table === 'occ'
      ? _.get(cache[oid], 'occ', [])
      : _.get(cache[oid], 'ind', []);

  if (!rows.length) return null;

  let selectedValue = null;

  if (measure.code) {
    const codeField = measure.table === 'occ' ? 'F2_DIGIT_SOC' : 'INDID';

    selectedValue = rows.find(r => _.get(r, ['attributes', codeField]) === measure.code);
  }

  return {
    emp_count: _.get(selectedValue, 'attributes.EMP', null),
    avg_wage: _.get(selectedValue, 'attributes.AVERAGE_ANNUAL_EARNINGS', null),
  };
}

// Get the appropriate value based on measure type
function getMetricValue(measureData, metricType) {
  if (!measureData) return null;
  return metricType === 'wages' ? measureData.avg_wage : measureData.emp_count;
}

// Create a discrete color scale using predefined colors
function createDiscreteColorScale(bounds, colors) {
  return value => {
    if (value == null) return '#cccccc';
    if (value < bounds[0]) return colors[0] || '#cccccc';
    for (let i = 1; i < bounds.length; i += 1) {
      if (value < bounds[i]) return colors[i] || colors[colors.length - 1] || '#cccccc';
    }
    // value >= last bound
    return colors[colors.length - 1] || '#cccccc';
  };
}

class WorkforceLayerComponent extends Component {
  static propTypes = {
    startLoading: PropTypes.func.isRequired,
    stopLoading: PropTypes.func.isRequired,
    selectedMeasure: PropTypes.shape({
      table: PropTypes.oneOf(['occ', 'ind']),
      code: PropTypes.number,
      metricType: PropTypes.oneOf(['wages', 'emp_count', 'employment']),
    }),
  };

  static contextTypes = {
    map: PropTypes.instanceOf(LeafletMap),
  };

  constructor(props) {
    super(props);
    this.state = { featureLayer: null };
    this.newOIDs = [];
    this.processedOIDs = new Set();
    this.relatedDataCache = {};
    this.colorScale = null;
    this.originalMinZoom = undefined;

    // Bind helpers
    this.waitForTargetZoom = this.waitForTargetZoom.bind(this);
    this.initLayerAfterReady = this.initLayerAfterReady.bind(this);
  }

  /* Ensure map is zoomed to MIN_WORKFORCE_ZOOM_LEVEL */
  waitForTargetZoom(map) {
    return new Promise(resolve => {
      if (!map) {
        resolve();
        return;
      }
      if (map.getZoom() >= MIN_WORKFORCE_ZOOM_LEVEL) {
        resolve();
        return;
      }
      // Register one-time handler before triggering the zoom to avoid missing the event
      map.once('zoomend', () => resolve());
      map.setZoom(MIN_WORKFORCE_ZOOM_LEVEL);
    });
  }

  /* Fetch token & zoom, then create layer */
  async initLayerAfterReady(map) {
    const { startLoading, stopLoading } = this.props;

    try {
      startLoading();
      // Kick off both async operations in parallel
      const tokenPromise = getArcGisToken();
      const zoomPromise = this.waitForTargetZoom(map);

      const [tokenResponse] = await Promise.all([tokenPromise, zoomPromise]);
      const token = tokenResponse.access_token || tokenResponse.token;

      this.initializeWorkforceLayer(token, map);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error preparing Workforce layer:', error);
      stopLoading();
    }
  }

  componentDidMount() {
    const { selectedMeasure } = this.props;

    // Don't load anything if no specific code is selected
    if (!_.get(selectedMeasure, 'code')) {
      return;
    }

    const map = _.get(this, 'context.map', null);
    if (map) {
      // Store original minZoom to restore it later
      this.originalMinZoom = map.options.minZoom;
      map.options.minZoom = MIN_WORKFORCE_ZOOM_LEVEL;
    }

    // Prepare and create the layer after required zoom + token are ready
    this.initLayerAfterReady(map);
  }

  componentDidUpdate(prevProps) {
    const { selectedMeasure } = this.props;
    const prevMeasure = prevProps.selectedMeasure;

    // Initialize layer when a code is selected for the first time
    if (_.get(selectedMeasure, 'code') && !_.get(prevMeasure, 'code')) {
      const map = _.get(this, 'context.map', null);

      if (map) {
        // Store original minZoom to restore it later
        this.originalMinZoom = map.options.minZoom;
        map.options.minZoom = MIN_WORKFORCE_ZOOM_LEVEL;
      }

      // Prepare and create the layer after required zoom + token are ready
      this.initLayerAfterReady(map);
      return;
    }

    // Remove layer when code is deselected
    if (!_.get(selectedMeasure, 'code') && _.get(prevMeasure, 'code')) {
      const layer = _.get(this, 'state.featureLayer', null);
      if (layer && layer.remove && layer._map) {
        layer.remove();
        this.setState({ featureLayer: null });
        this.newOIDs = [];
        this.processedOIDs = new Set();
        this.relatedDataCache = {};
        this.colorScale = null;
      }

      // Reset min zoom restriction
      this.resetMapZoom();
      return;
    }

    // Update colors when measure changes but still has a code
    if (_.get(selectedMeasure, 'code') && this.state.featureLayer &&
        (_.get(selectedMeasure, 'code') !== _.get(prevMeasure, 'code') ||
         _.get(selectedMeasure, 'table') !== _.get(prevMeasure, 'table') ||
         _.get(selectedMeasure, 'metricType') !== _.get(prevMeasure, 'metricType'))) {
      this.updateMapColors();
      this.applyTooltips(this.state.featureLayer);
    }
  }

  componentWillUnmount() {
    const layer = _.get(this, 'state.featureLayer', null);
    if (layer && layer.remove && layer._map) layer.remove();

    // Reset min zoom restriction when unmounting
    this.resetMapZoom();
  }

  // Helper to reset map zoom restriction
  resetMapZoom() {
    const map = _.get(this, 'context.map', null);
    if (map) {
      // If we stored the original minZoom, restore it
      if (this.originalMinZoom !== undefined) {
        map.options.minZoom = this.originalMinZoom;
      } else {
        // Otherwise use a safe default (DEFAULT_ZOOM_LEVEL or 0)
        map.options.minZoom = 0;
      }
    }
  }

  onEachFeature = feature => {
    if (!feature) return;
    const oid = _.get(feature, 'properties.OBJECTID', null);
    if (oid && !this.processedOIDs.has(oid)) this.newOIDs.push(oid);
  };

  getFeatureStyle = feature => {
    const baseStyle = {
      weight: 1,
      fillOpacity: 0.6,
      opacity: 0.5,
      color: '#000000',
      fillColor: '#cccccc',
    };

    const oid = _.get(feature, 'properties.OBJECTID', null);
    // Don't apply color if no measure is selected, no code is specified, or colorScale isn't ready
    if (!oid || !this.colorScale || !this.props.selectedMeasure || !_.get(this.props.selectedMeasure, 'code')) return baseStyle;

    const measureData = getMeasureValue(oid, this.props.selectedMeasure, this.relatedDataCache);
    const val = getMetricValue(measureData, this.props.selectedMeasure.metricType);
    if (val == null) return baseStyle;

    return { ...baseStyle, fillColor: this.colorScale(val) };
  };

  /**
   * Attach a tooltip for each feature
   * @param {object} layer Leaflet layer
   */
  setTooltip(layer) {
    const oid = _.get(layer, 'feature.properties.OBJECTID', null);
    if (!oid) return;

    // const data = _.get(this.relatedDataCache, oid, {});
    // const ind = _.get(data, 'ind', []);

    const zip = _.get(layer, 'feature.properties.ZIP_CODE', 'Unknown ZIP');
    const measureData = getMeasureValue(oid, this.props.selectedMeasure, this.relatedDataCache);
    const measureValue = getMetricValue(measureData, this.props.selectedMeasure.metricType);
    const measureText = measureValue == null
      ? 'No data'
      : this.props.selectedMeasure.metricType === 'wages'
        // Wages: always show two decimal places (nearest cent)
        ? `$${Number(measureValue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        // Employment counts: round up to nearest integer before formatting
        : humanizeNumber(Math.ceil(measureValue));
    const metricLabel =
      this.props.selectedMeasure.metricType === 'wages' ? 'Average Wage' : 'Employment Count';

    let measureName = 'None';
    if (this.props.selectedMeasure) {
      const { table, code } = this.props.selectedMeasure;
      if (code) {
        measureName = table === 'occ' ? OCCUPATION_DICT[code] : INDUSTRY_DICT[code];
      } else {
        measureName = 'All';
      }
    }

    const tooltipHtml = `
      <div>
        <b>ZIP CODE:</b> ${zip}<br/>
        <hr/>
        <b>Measure:</b> ${measureName}<br/>
        <b>${metricLabel}:</b> ${measureText}<br/>
      </div>
    `;
    if (layer.bindTooltip) layer.bindTooltip(tooltipHtml, { sticky: true });
  }

  /**
   * Loop through features and attach tooltips
   * (kept separate to satisfy react/sort-comp ordering)
   */
  applyTooltips(layer) {
    if (!layer) return;
    layer.eachFeature(l => this.setTooltip(l));
  }

  updateMapColors() {
    const layer = _.get(this, 'state.featureLayer', null);
    if (!layer) return;

    const { selectedMeasure } = this.props;
    if (!selectedMeasure) {
      this.colorScale = null;
      layer.setStyle(this.getFeatureStyle);
      return;
    }

    const metricKey = selectedMeasure.metricType === 'employment' ? 'emp_count' : selectedMeasure.metricType;
    const bounds = WORKFORCE_LEGEND_BOUNDS[metricKey] || [];
    const colors = WORKFORCE_COLOR_SCALES[metricKey] || [];

    if (!bounds.length || !colors.length) {
      this.colorScale = null;
      layer.setStyle(this.getFeatureStyle);
      return;
    }

    this.colorScale = createDiscreteColorScale(bounds, colors);
    layer.setStyle(this.getFeatureStyle);
  }

  handleLoad = async e => {
    const { stopLoading } = this.props;
    if (!this.newOIDs.length) {
      this.updateMapColors();
      this.applyTooltips(e.target);
      stopLoading();
      return;
    }

    try {
      const dataById = await fetchOccAndInd(this.newOIDs, this.arcgisToken);
      this.newOIDs.forEach(oid => {
        this.processedOIDs.add(oid);
        this.relatedDataCache[oid] = dataById[oid];
      });
      this.newOIDs = [];
      this.updateMapColors();
      this.applyTooltips(e.target);
      stopLoading();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Bulk fetch error:', err);
      stopLoading();
    }
  };

  initializeWorkforceLayer(token, map) {
    const { startLoading, stopLoading } = this.props;

    try {
      const workforceLayer = Esri.featureLayer({
        url: esriWorkforceServer,
        token,
        precision: 5,
        style: this.getFeatureStyle,
        onEachFeature: this.onEachFeature,
      })
        .on('loading', () => startLoading())
        .on('load', this.handleLoad);

      if (map) workforceLayer.addTo(map);
      this.setState({ featureLayer: workforceLayer });
      this.arcgisToken = token;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create workforce layer:', err);
      stopLoading();
    }
  }

  render() {
    const { selectedMeasure } = this.props;
    if (!selectedMeasure || !selectedMeasure.code) return null;

    return (
      <LegendBar metricType={selectedMeasure.metricType || 'emp_count'} />
    );
  }
}

const mapDispatchToProps = dispatch => ({
  startLoading: () => dispatch(activateMapLoadingStatus()),
  stopLoading: () => dispatch(deactivateMapLoadingStatus()),
});

const mapStateToProps = state => ({
  selectedMeasure: state.selectedWorkforceMeasure,
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkforceLayerComponent);



// WEBPACK FOOTER //
// ./src/components/Map/Layers/Workforce.js